import { Link } from "react-router-dom";
import { Section } from "@/components/ui/Section";
import { ArrowRight, Shield, LineChart, Sparkles, Target, Gauge, Layers } from "lucide-react";

export default function Landing() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-x relative pt-24 pb-28 md:pt-32 md:pb-36">
          <div className="eyebrow mb-5">// INFINI · X &nbsp;//&nbsp; Trust · Leads · ROI</div>
          <h1 className="display-xl text-silver-50 max-w-5xl">
            Build trustworthy brands.<br />
            <span className="text-brand-blue">Generate leads.</span>{" "}
            <span className="text-brand-red">Measure everything.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-silver-200 leading-relaxed">
            INFINI X is the operating system behind a new kind of marketing agency —
            one that phase-gates advertising behind real brand strength, so every dollar
            compounds instead of leaks.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-primary">Book a Strategy Call <ArrowRight size={16} /></Link>
            <Link to="/process" className="btn-ghost">See the Process</Link>
          </div>

          {/* stat strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-700/50 border border-ink-700/60 rounded-2xl overflow-hidden">
            {[
              { k: "7.9x", v: "Blended ROAS" },
              { k: "-42%", v: "Avg CPL reduction" },
              { k: "75+", v: "Brand Strength gate" },
              { k: "14", v: "Services phased P1–P3" },
            ].map((s) => (
              <div key={s.v} className="bg-ink-900/80 p-6">
                <div className="font-display font-extrabold text-3xl md:text-4xl text-silver-50">{s.k}</div>
                <div className="eyebrow mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <Section
        eyebrow="// Primary Directive"
        title={<>Trust first. Then leads. <span className="text-brand-blue">Always measured.</span></>}
        subtitle="Most agencies sell ads into brands that can't hold them. We refuse. Every client crosses a Brand Strength gate before paid media turns on."
      >
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { icon: Shield, title: "Trust Architecture", body: "Reviews, reputation, social proof, and content consistency built before ads run." },
            { icon: Target, title: "Lead Engine", body: "SEO, AEO, funnels, and paid media layered on top of a brand that can actually convert." },
            { icon: LineChart, title: "ROI Intelligence", body: "Every touchpoint measured. CPL, ROAS, LTV, NPS — rolled up into strategic decisions." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="panel p-8 corner-bracket">
              <Icon className="text-brand-blue" size={28} />
              <h3 className="display-md mt-5 text-silver-50">{title}</h3>
              <p className="mt-3 text-silver-200 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CAPABILITIES */}
      <Section
        eyebrow="// What we run for you"
        title="One platform. Every growth lever."
      >
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { icon: Sparkles, t: "Spark AI Strategist", d: "11-stage brand discovery. ICP, USP, voice, sitemap, wireframe, launch plan." },
            { icon: Layers, t: "Service Catalog", d: "14 services across Foundation / Growth / Retention with effort, dependencies, budgets." },
            { icon: Gauge, t: "Brand Strength Score", d: "Live weighted score with weakness diagnosis and projected CPL impact." },
            { icon: Target, t: "Content Engine", d: "Single, bulk, and scheduled generation tuned to your verified brand voice." },
            { icon: LineChart, t: "ROI Dashboard", d: "Closed-loop attribution from first touch to revenue, with predictive lift." },
            { icon: Shield, t: "Reputation Core", d: "Review velocity, response playbooks, testimonial capture pipeline." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="panel p-6 hover:border-brand-blue/60 transition">
              <div className="flex items-center gap-3">
                <Icon className="text-brand-blue" size={20} />
                <h4 className="font-display font-bold uppercase tracking-wider text-silver-50">{t}</h4>
              </div>
              <p className="mt-3 text-silver-200 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="panel-hi p-10 md:p-14 text-center relative corner-bracket">
          <div className="eyebrow">// Ready to see your Brand Strength Score?</div>
          <h2 className="display-lg text-silver-50 mt-3">
            We'll run a <span className="text-brand-blue">free 60-minute audit</span>{" "}
            and show you exactly <span className="text-brand-red">where leads are leaking.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/contact" className="btn-primary">Book Strategy Call <ArrowRight size={16} /></Link>
            <Link to="/services" className="btn-ghost">View Services</Link>
          </div>
        </div>
      </Section>
    </>
  );
}
